import {Injectable} from '@angular/core';
import {LexInvoice} from '../../../../../database-models';

/**
 * Offene Forderungen aus Rechnungen: nur als überfällig markierte Rechnungen zählen – deren unbezahlter Betrag
 * und deren offene Mahngebühr. Ohne Markierung wird nichts angemahnt (auch keine bereits hinterlegte Gebühr).
 * Gemeinsame Rechenlogik für den Hinweis-Banner und die Rechnungsübersicht in "Mein Unternehmen".
 */
@Injectable({providedIn: 'root'})
export class InvoiceDuesService {

  /**
   * Rechnungsbetrag, der noch offen ist: nur als überfällig markierte Rechnungen,
   * nie bezahlte oder stornierte (auch wenn die Markierung noch nicht entfernt wurde)
   */
  public invoiceDue(invoice: LexInvoice): number {
    const settled = this.isPaid(invoice) || invoice?.voucherStatus === 'voided';
    return invoice?.markedOverdue && !settled ? Number(invoice.brutto) || 0 : 0;
  }

  /** offene Mahngebühr einer markierten Rechnung; "in Prüfung" zählt nicht, da die Rechnung bereits bezahlt wurde */
  public feeDue(invoice: LexInvoice): number {
    return invoice?.markedOverdue && invoice.dunningFee && invoice.dunningFeeStatus === 'open' ? Number(invoice.dunningFee) || 0 : 0;
  }

  /** Rechnung laut Lexware bezahlt */
  public isPaid(invoice: LexInvoice): boolean {
    return invoice?.voucherStatus === 'paid' || invoice?.voucherStatus === 'paidoff';
  }

  /**
   * Hauptforderung beglichen, offen ist nur noch die Mahngebühr.
   * Nur wenn die Rechnung wirklich bezahlt ist – eine offene, (noch) nicht als überfällig markierte Rechnung zählt nicht dazu.
   */
  public onlyFeeDue(invoice: LexInvoice): boolean {
    return this.feeDue(invoice) > 0 && this.invoiceDue(invoice) === 0 && this.isPaid(invoice);
  }

  /** alle offenen Forderungen sind reine Mahngebühren */
  public onlyFeesDue(invoices: LexInvoice[]): boolean {
    const due = (invoices || []).filter(invoice => this.due(invoice) > 0);
    return due.length > 0 && due.every(invoice => this.onlyFeeDue(invoice));
  }

  public due(invoice: LexInvoice): number {
    return this.invoiceDue(invoice) + this.feeDue(invoice);
  }

  public total(invoices: LexInvoice[]): number {
    return (invoices || []).reduce((sum, invoice) => sum + this.due(invoice), 0);
  }

  public feesTotal(invoices: LexInvoice[]): number {
    return (invoices || []).reduce((sum, invoice) => sum + this.feeDue(invoice), 0);
  }

  public feeStatusLabel(invoice: LexInvoice): string {
    switch (invoice?.dunningFeeStatus) {
      case 'open':
        return 'offen';
      case 'review':
        return 'in Prüfung';
      case 'paid':
        return 'beglichen';
      default:
        return '';
    }
  }
}
